import { useEffect, useState } from 'react';
import { api, getUserId, LEVEL_LABELS } from '@/lib/api';
import { Trophy, Medal } from 'lucide-react';

export default function Leaderboard({ currentUserId }) {
  const [period, setPeriod] = useState('global');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const userId = currentUserId || getUserId();

  useEffect(() => {
    setLoading(true);
    api.get('/leaderboard', { params: { period, userId } })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [period, userId]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-4 h-4 text-[#FFD700]" />;
    if (rank === 2) return <Medal className="w-4 h-4 text-[#C0C0C0]" />;
    if (rank === 3) return <Medal className="w-4 h-4 text-[#CD7F32]" />;
    return <span className="text-[#4B5563] text-sm font-mono w-4 text-center">#{rank}</span>;
  };

  return (
    <div className="glass-card rounded-2xl p-4 sm:p-6 mt-4 sm:mt-6 animate-fade-up" data-testid="leaderboard">
      <h3 className="font-outfit font-bold text-lg sm:text-xl text-white mb-3 sm:mb-4 flex items-center gap-2">
        <Trophy className="w-4 sm:w-5 h-4 sm:h-5 text-[#FFD700]" />
        Leaderboard
      </h3>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/5 rounded-lg p-1 mb-4 sm:mb-5">
        {[{ key: 'global', label: 'Global' }, { key: 'month', label: 'Dieser Monat' }].map(tab => (
          <button
            key={tab.key}
            onClick={() => setPeriod(tab.key)}
            data-testid={`leaderboard-tab-${tab.key}`}
            className={`flex-1 py-1.5 sm:py-2 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-outfit font-medium transition-all
              ${period === tab.key
                ? 'bg-[#3B82F6]/20 text-[#60A5FA]'
                : 'text-[#9CA3AF] hover:text-white'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center text-[#9CA3AF] py-4 sm:py-6 animate-pulse text-sm">Lade Rangliste...</div>
      ) : !data || data.entries.length === 0 ? (
        <div className="text-center text-[#4B5563] py-4 sm:py-6 text-sm">Noch keine Einträge</div>
      ) : (
        <>
          {/* Table */}
          <div className="space-y-1 sm:space-y-1.5">
            {/* Header */}
            <div className="grid grid-cols-12 gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs text-[#4B5563] font-mono uppercase">
              <div className="col-span-1">#</div>
              <div className="col-span-4">Spieler</div>
              <div className="col-span-3 text-right">Richtig</div>
              <div className="col-span-4 text-right">Score</div>
            </div>

            {data.entries.map((entry) => {
              const isMe = entry.userId === userId;
              return (
                <div
                  key={entry.rank}
                  data-testid={`leaderboard-row-${entry.rank}`}
                  className={`grid grid-cols-12 gap-1 sm:gap-2 items-center px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg transition-all
                    ${isMe
                      ? 'bg-[#3B82F6]/15 border border-[#3B82F6]/30'
                      : 'bg-white/3 hover:bg-white/5'
                    }`}
                >
                  <div className="col-span-1 flex items-center">
                    {getRankIcon(entry.rank)}
                  </div>
                  <div className="col-span-4 truncate">
                    <span className={`text-xs sm:text-sm font-manrope ${isMe ? 'text-[#60A5FA] font-bold' : 'text-white'}`}>
                      {entry.nickname || `Spieler ${entry.userId?.slice(0, 4)}`}
                      {isMe && <span className="ml-1 text-[10px] sm:text-xs text-[#3B82F6]">(Du)</span>}
                    </span>
                  </div>
                  <div className="col-span-3 text-right text-xs sm:text-sm text-[#9CA3AF] font-mono">
                    {entry.percentCorrect}%
                  </div>
                  <div className="col-span-4 text-right">
                    <span className={`text-xs sm:text-sm font-mono font-bold ${isMe ? 'text-[#60A5FA]' : 'text-white'}`}>
                      {entry.rankingScore}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* User rank if not in top 10 */}
          {data.userRank && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/5">
              <div className="grid grid-cols-12 gap-1 sm:gap-2 items-center px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg bg-[#3B82F6]/10 border border-[#3B82F6]/20">
                <div className="col-span-1 text-[#60A5FA] text-xs sm:text-sm font-mono">#{data.userRank.rank}</div>
                <div className="col-span-4 text-[#60A5FA] text-xs sm:text-sm font-bold">Du</div>
                <div className="col-span-3 text-right text-xs sm:text-sm text-[#9CA3AF] font-mono">{data.userRank.percentCorrect}%</div>
                <div className="col-span-4 text-right text-xs sm:text-sm text-[#60A5FA] font-mono font-bold">{data.userRank.rankingScore}</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
